import { pickUserAttribute } from '../UserAttribute';
import { EventType } from '../UsageEvent';
import { pickAwareEvent, pickUserEvent, pickUsageTracker, pickSystemEvent, } from '..';
class UsageHelperImpl {
    constructor(feature) {
        this.usageUser = {};
        this.feature = '';
        this.latestEvents = {};
        this.lastActiveTimeKey = '__Core.UserAttribute.lastActiveTime__';
        this.sessionKey = '__Core.UserAttribute.sessionId__';
        this.userIdKey = '__Core.UserAttribute.UsageUserId__';
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        this.Fork = (feature) => {
            const newUsageHelper = new UsageHelperImpl(feature);
            // Fix for micro-fe not aligning to the previous page-tracking from parent
            newUsageHelper.latestEvents = Object.assign({}, this.latestEvents);
            newUsageHelper.SetUserRef(this.usageUser);
            if (this.usageConfig)
                newUsageHelper.SetUsageConfig(this.usageConfig);
            return newUsageHelper;
        };
        this.ForkTelemetryContext = (feature, source) => {
            const newUsageHelper = new UsageHelperImpl(feature);
            // Fix for micro-fe not aligning to the previous page-tracking from parent
            newUsageHelper.latestEvents = Object.assign({}, this.latestEvents);
            newUsageHelper.SetUserRef(this.usageUser);
            if (this.usageConfig)
                newUsageHelper.SetUsageConfig(this.usageConfig);
            const newContext = {
                sourceComponent: feature,
                sourceScript: source,
                setUsageEvent: newUsageHelper.MassageEvent,
                setUsageUser: (usageUser) => {
                    UsageHelper.SetUser(usageUser);
                    return usageUser;
                },
                usageUser: newUsageHelper.GetUser,
                setUsageConfig: newUsageHelper.SetUsageConfig,
                getChildContext: newUsageHelper.ForkTelemetryContext,
            };
            return newContext;
        };
        this.SetUserRef = (userAttribute) => {
            //Do not spread or clone the user attribute. The forked helpers point to the same reference.
            this.usageUser = userAttribute;
            this.checkSession();
        };
        this.SetUser = (userAttribute) => {
            var _a;
            //Do not spread or clone the user attribute. The forked helpers point to the same reference.
            this.usageUser.lastActiveTime = userAttribute.lastActiveTime;
            this.usageUser.sessionId = userAttribute.sessionId;
            this.usageUser.usageUserId = userAttribute.usageUserId;
            if (this.usageUser.usageUserId === 'Error')
                this.usageUser.usageUserId =
                    (_a = localStorage.getItem(this.userIdKey)) !== null && _a !== void 0 ? _a : 'Error';
            this.checkSession();
        };
        this.SetUsageConfig = (config) => {
            this.usageConfig = config;
            this.checkSession();
        };
        this.GetUser = () => {
            return Object.assign({}, this.usageUser);
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.parseName = (e) => {
            const value = `${e.feature || this.feature}-${e.subFeature || '$inherited$'}-${e.type}-${e.eventName || '$inherited$'}`;
            const featureKey = e.feature || this.feature;
            const subFeatureKey = e.subFeature
                ? featureKey + '-' + e.subFeature
                : '';
            const eventNameKey = e.eventName
                ? featureKey + '-' + (e.subFeature || 'NA') + '-' + e.eventName
                : '';
            return {
                key: eventNameKey || subFeatureKey || featureKey,
                value,
                featureKey,
                subFeatureKey,
                eventNameKey,
            };
        };
        this.getLatestEvent = (e) => {
            const { key, subFeatureKey, featureKey, eventNameKey } = this.parseName(e);
            //Page load key
            if (e.eventName == 'PageLoad') {
                return Object.assign({}, this.latestEvents[key.replace('PageLoad', 'PageLoadStart')]);
            }
            // Climb the hierarchy to fetch key
            const past = new Date('0001-01-01T00:00:00Z');
            const closestMatchKey = Object.keys(this.latestEvents)
                .filter((a) => a.startsWith(key + '-'))
                .reduce((prev, curr) => {
                var _a, _b;
                const previous = ((_a = this.latestEvents[prev]) === null || _a === void 0 ? void 0 : _a.eventDate) || past;
                const current = ((_b = this.latestEvents[curr]) === null || _b === void 0 ? void 0 : _b.eventDate) || past;
                return current > previous ? curr : prev;
            }, '');
            const caseIgnorantFeature = Object.keys(this.latestEvents).find((key) => key.toLowerCase() === featureKey.toLowerCase()) || '';
            const latest = this.latestEvents[key] || // If there is a head-on match to key
                this.latestEvents[eventNameKey] || // If there is a closest event based key
                this.latestEvents[subFeatureKey] || // If there is a closest subfeature based key
                this.latestEvents[featureKey] || // If there is a closest feature based key
                this.latestEvents[closestMatchKey] || // IF there is a closes match by hierarchy travel
                this.latestEvents[this.feature] || // If there is no match at all, then send the latest helper event
                this.latestEvents[caseIgnorantFeature]; // Fallback to feature name searched by case-insensitive search
            return Object.assign({}, latest);
        };
        this.MassageEvent = (event) => {
            var _a;
            const eventCopy = Object.assign({}, event);
            const name = this.parseName(event);
            this.checkSession();
            if (!eventCopy.type)
                return eventCopy;
            let usageLog = {}; // Let's prepare the usageLog one step at a time.
            usageLog.name = name.value;
            usageLog.eventDate = event.eventDate || new Date();
            switch (eventCopy.type) {
                case EventType.Aware:
                    const awareEvent = Object.assign({}, pickAwareEvent(eventCopy));
                    this.latestEvents[EventType.Aware] = awareEvent;
                    this.usageUser = pickUserAttribute(Object.assign(Object.assign({}, this.usageUser), awareEvent));
                    if ('sessionId' in eventCopy)
                        this.usageUser.sessionId = eventCopy.sessionId; //Retain the input session-id
                    usageLog = awareEvent;
                    break;
                case EventType.User:
                    //Lets build a new UsageLog from the user's event
                    const latestEvent = this.getLatestEvent(eventCopy);
                    const currentFeature = eventCopy.feature || (latestEvent === null || latestEvent === void 0 ? void 0 : latestEvent.feature) || this.feature; // Deduce current feature
                    usageLog = Object.assign(Object.assign({}, usageLog), pickUserAttribute(this.usageUser || eventCopy)); // Start with User Attributes
                    usageLog = Object.assign(Object.assign(Object.assign({}, usageLog), pickUserEvent(eventCopy)), { feature: currentFeature });
                    if (eventCopy.eventName === 'PageLoadStart') {
                        usageLog.pageTrackingId = this.Guid();
                        usageLog.startTime = new Date();
                    }
                    else if (eventCopy.eventName === 'PageLoad') {
                        // TODO: ras1 => remove hard-coding
                        usageLog.pageTrackingId = (latestEvent === null || latestEvent === void 0 ? void 0 : latestEvent.startTime)
                            ? latestEvent.pageTrackingId
                            : this.Guid();
                        usageLog.timeTaken = eventCopy.timeTaken
                            ? eventCopy.timeTaken
                            : +new Date() - +((latestEvent === null || latestEvent === void 0 ? void 0 : latestEvent.startTime) || new Date());
                    }
                    else {
                        const latestUserEvent = this.getLatestEvent(eventCopy);
                        if (latestUserEvent) {
                            const tracker = Object.assign({}, pickUsageTracker(latestUserEvent));
                            usageLog.pageTrackingId = tracker.pageTrackingId;
                        }
                    }
                    usageLog.actionTrackingId = this.Guid(); //Attach a uniqueGuid for the action.
                    const newKey = this.parseName(Object.assign(Object.assign({}, eventCopy), { feature: currentFeature }));
                    this.latestEvents[newKey.key] = usageLog;
                    this.latestEvents[this.feature] = usageLog; // Store a copy of the latest event to the shell object
                    break;
                case EventType.System:
                    const latestUserEvent = this.getLatestEvent(eventCopy);
                    //Lets build a new UsageLog from the system event
                    usageLog = Object.assign(Object.assign(Object.assign({}, usageLog), pickUserAttribute((this.usageUser || eventCopy))), pickSystemEvent(eventCopy));
                    //TODO: How to handle a new page load event ?
                    // if (!this.getLatestEvent(eventCopy)) {
                    //}
                    const userEvent = pickUserEvent(latestUserEvent);
                    const tracker = pickUsageTracker(latestUserEvent);
                    usageLog.pageTrackingId = tracker.pageTrackingId;
                    usageLog.correlationTrackingId = tracker.actionTrackingId;
                    if (!usageLog.feature) {
                        usageLog.feature =
                            userEvent.feature || 'NA'; //TODO: Test case input shouldnt be modified
                    }
                    if (!usageLog.subFeature) {
                        usageLog.subFeature =
                            userEvent.subFeature || 'NA'; //TODO: Test case input shouldnt be modified
                    }
                    if (!usageLog.eventName) {
                        usageLog.eventName = userEvent.eventName;
                    }
                    break;
            }
            const newKey = this.parseName(usageLog);
            usageLog.name = newKey.value;
            usageLog.inheritedName = name.value;
            /* istanbul ignore next */
            if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
                this.consoleUsageLog(newKey.value, usageLog);
            }
            if ('sessionId' in eventCopy)
                usageLog.sessionId = eventCopy.sessionId; //Retain the input session-id
            usageLog.usageVersion =
                ((_a = eventCopy) === null || _a === void 0 ? void 0 : _a.usageVersion) || '1.0';
            return usageLog;
        };
        this.diffInMinutes = (date1, date2) => {
            let diff = (date1.getTime() - date2.getTime()) / 1000;
            diff /= 60;
            return Math.abs(Math.round(diff));
        };
        this.feature = feature;
    }
    /* istanbul ignore next */
    consoleUsageLog(name, usageLogData) {
        const userEventStyle = 'color: #FFA500; padding: 2px 5px;';
        const systemEventStyle = 'color: #c46210; padding: 2px 5px';
        const boldFont = 'font-weight: bold;';
        const eventStyle = usageLogData.type === EventType.User
            ? userEventStyle
            : systemEventStyle;
        console.group('%cusage event %c %s', eventStyle, boldFont, name);
        console.dir(usageLogData);
        console.groupEnd();
    }
    checkSession() {
        var _a;
        //Do not manage session until usageConfig is materialized
        if (this.usageConfig) {
            const lastActive = new Date(localStorage.getItem(this.lastActiveTimeKey) || new Date());
            const sessionId = localStorage.getItem(this.sessionKey);
            const duration = this.diffInMinutes(lastActive, new Date());
            if (duration < (((_a = this.usageConfig) === null || _a === void 0 ? void 0 : _a.sessionDurationMinutes) || -1) &&
                sessionId) {
                this.usageUser.sessionId = sessionId;
            }
            else {
                this.usageUser.sessionId = this.Guid();
                localStorage.setItem(this.sessionKey, this.usageUser.sessionId);
            }
            localStorage.setItem(this.lastActiveTimeKey, new Date().toString());
        }
    }
    Guid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0, v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
export const UsageHelper = new UsageHelperImpl('Shell');
//# sourceMappingURL=Usage.helper.js.map